import { createContext, useContext, useState, type ReactNode } from 'react';

type Lang = 'vi' | 'en' | 'de';

type Dict = Record<string, string>;

const dicts: Record<Lang, Dict> = {
  vi: {
    'app.name': 'Personal OS',
    'nav.dashboard': 'Tổng quan',
    'nav.tasks': 'Tasks',
    'nav.notes': 'Notes',
    'nav.areas': 'Areas',
    'nav.workspaces': 'Workspaces',
    'nav.logout': 'Đăng xuất',
    'login.title': 'Đăng nhập Personal OS',
    'login.email': 'Email',
    'login.password': 'Mật khẩu',
    'login.submit': 'Đăng nhập',
    'login.failed': 'Sai email hoặc mật khẩu',
    'capture.placeholder': 'Capture nhanh — gõ rồi Enter…',
    'capture.success': 'Đã lưu vào Inbox',
    'dashboard.appLauncher': 'App Launcher',
    'dashboard.appLauncher.empty': 'Chưa có site nào — thêm tại Workspaces',
    'dashboard.today': 'Top 3 hôm nay',
    'dashboard.today.empty': 'Hôm nay không có task ưu tiên',
    'dashboard.areas': 'Phân vùng',
    'dashboard.inbox': 'Inbox',
    'dashboard.inbox.empty': 'Inbox trống',
    'workspaces.title': 'Workspaces',
    'workspaces.add': 'Thêm site mới',
    'workspaces.form.name': 'Tên hiển thị',
    'workspaces.form.url': 'URL',
    'workspaces.form.url.help': 'Đường dẫn đầy đủ, vd: https://office.asiasupermarkt-th.de',
    'workspaces.form.type': 'Loại',
    'workspaces.form.area': 'Thuộc Area',
    'workspaces.form.color': 'Màu',
    'workspaces.form.icon': 'Icon (Lucide name)',
    'workspaces.form.submit': 'Lưu site',
    'workspaces.form.cancel': 'Huỷ',
    'workspaces.created': 'Đã thêm site',
    'workspaces.archived': 'Đã ẩn site',
    'workspaces.empty': 'Chưa có workspace nào — thêm site đầu tiên',
    'tasks.title': 'Tasks',
    'tasks.add': 'Tạo task',
    'tasks.complete': 'Đánh dấu xong',
    'tasks.empty': 'Chưa có task',
    'notes.title': 'Notes',
    'notes.add': 'Tạo note',
    'notes.empty': 'Chưa có note',
    'areas.title': 'Areas',
    'areas.add': 'Thêm area',
    'areas.empty': 'Chưa có area',
    'common.cancel': 'Huỷ',
    'common.save': 'Lưu',
    'common.delete': 'Xoá',
    'common.loading': 'Đang tải…',
  },
  en: {
    'app.name': 'Personal OS',
    'login.title': 'Sign in to Personal OS',
  },
  de: {
    'app.name': 'Personal OS',
    'login.title': 'Bei Personal OS anmelden',
  },
};

type LangCtx = {
  lang: Lang;
  setLang: (l: Lang) => void;
  t: (key: string) => string;
};

const Ctx = createContext<LangCtx | null>(null);

export function LangProvider({ children }: { children: ReactNode }) {
  const [lang, setLang] = useState<Lang>((localStorage.getItem('myos.lang') as Lang) ?? 'vi');
  const t = (key: string) => dicts[lang][key] ?? dicts.vi[key] ?? key;
  const update = (l: Lang) => {
    setLang(l);
    localStorage.setItem('myos.lang', l);
  };
  return <Ctx.Provider value={{ lang, setLang: update, t }}>{children}</Ctx.Provider>;
}

export function useLang(): LangCtx {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useLang must be inside LangProvider');
  return ctx;
}

export function useT(): (key: string) => string {
  return useLang().t;
}
